// 🤝 PartnersPreview - Aperçu des partenaires du festival
// Affiche un échantillon des partenaires par catégorie (institutionnels, médias, techniques)
// Utilisé dans : src/pages/HomePage.jsx

import { Award, ArrowRight, Building2, Radio, Wrench } from 'lucide-react'
import { Link } from 'react-router-dom'
import partnersData from '@/locales/partners.json'

/**
 * Composant de prévisualisation des partenaires
 * Affiche les 3 catégories de partenaires avec lien vers la page complète
 * Les données proviennent du fichier JSON partners.json
 */
export default function PartnersPreview() {
  const { hero, preview, categories } = partnersData

  return (
    <section className="section-padding bg-neutral-50 dark:bg-neutral-800">
      <div className="container-custom">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-display text-primary-600 dark:text-primary-400 mb-4">
            {hero.title}
          </h2>
          <p className="text-lg text-neutral-700 dark:text-neutral-300 max-w-2xl mx-auto">
            {preview.subtitle}
          </p>
        </div>

        {/* Grille des catégories */}
        <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto mb-12">
          {/* Institutionnels */}
          <div className="bg-white dark:bg-neutral-900 rounded-2xl p-6 shadow-lg border border-neutral-200 dark:border-neutral-700 hover:shadow-xl transition-shadow">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-primary-100 dark:bg-primary-950 rounded-xl">
                <Building2 className="text-primary-600 dark:text-primary-400" size={24} />
              </div>
              <h3 className="font-bold text-lg text-neutral-900 dark:text-neutral-100">
                {preview.categories.institutional}
              </h3>
            </div>
            <ul className="space-y-2">
              {categories.institutional.map((partner, index) => (
                <li key={index} className="flex items-center gap-2 text-sm text-neutral-700 dark:text-neutral-300">
                  <span className="w-1.5 h-1.5 bg-primary-500 rounded-full" />
                  {typeof partner === 'string' ? partner : partner.name}
                </li>
              ))}
            </ul>
          </div>

          {/* Médias */}
          <div className="bg-white dark:bg-neutral-900 rounded-2xl p-6 shadow-lg border border-neutral-200 dark:border-neutral-700 hover:shadow-xl transition-shadow">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-primary-100 dark:bg-primary-950 rounded-xl">
                <Radio className="text-primary-600 dark:text-primary-400" size={24} />
              </div>
              <h3 className="font-bold text-lg text-neutral-900 dark:text-neutral-100">
                {preview.categories.media}
              </h3>
            </div>
            <ul className="space-y-2">
              {categories.media.map((partner, index) => (
                <li key={index} className="flex items-center gap-2 text-sm text-neutral-700 dark:text-neutral-300">
                  <span className="w-1.5 h-1.5 bg-primary-500 rounded-full" />
                  {typeof partner === 'string' ? partner : partner.name}
                </li>
              ))}
            </ul>
          </div>

          {/* Techniques */}
          <div className="bg-white dark:bg-neutral-900 rounded-2xl p-6 shadow-lg border border-neutral-200 dark:border-neutral-700 hover:shadow-xl transition-shadow">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-primary-100 dark:bg-primary-950 rounded-xl">
                <Wrench className="text-primary-600 dark:text-primary-400" size={24} />
              </div>
              <h3 className="font-bold text-lg text-neutral-900 dark:text-neutral-100">
                {preview.categories.technical}
              </h3>
            </div>
            <ul className="space-y-2">
              {categories.technical.map((partner, index) => (
                <li key={index} className="flex items-center gap-2 text-sm text-neutral-700 dark:text-neutral-300">
                  <span className="w-1.5 h-1.5 bg-primary-500 rounded-full" />
                  {typeof partner === 'string' ? partner : partner.name}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* CTA vers page partenaires */}
        <div className="text-center">
          <Link
            to="/partners"
            className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-white px-8 py-4 rounded-lg font-bold text-lg transition-all shadow-xl hover:scale-105 group"
          >
            <Award size={24} />
            {preview.ctaButton}
            <ArrowRight className="group-hover:translate-x-1 transition-transform" size={20} />
          </Link>
        </div>
      </div>
    </section>
  )
}
